import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Target, TrendingUp, TrendingDown, Sparkles, Info, Settings, CheckCircle2, AlertCircle, FlaskConical, FileText } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ProfileForm from '@/components/ProfileForm';

const CAT_COLOR = {
  excelente: 'bg-green-100 text-green-800 border-green-200',
  buena: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  regular: 'bg-amber-100 text-amber-800 border-amber-200',
  condicionada: 'bg-orange-100 text-orange-800 border-orange-200',
  no_recomendable: 'bg-red-100 text-red-800 border-red-200',
};
const LEVEL_COLOR = {
  avanzado: 'bg-blue-900 text-white',
  completo: 'bg-blue-700 text-white',
  funcional: 'bg-blue-500 text-white',
  basico: 'bg-amber-500 text-white',
  insuficiente: 'bg-slate-400 text-white',
};

export default function IndustryScoreCard({ analysisId, businessProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sensitivity, setSensitivity] = useState(null);
  const [runningSensitivity, setRunningSensitivity] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/analyses/${analysisId}/industry-score`, {});
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error calculando score'); }
    finally { setLoading(false); }
  };

  const runSensitivity = async () => {
    setRunningSensitivity(true);
    try {
      const r = await api.post(`/analyses/${analysisId}/sensitivity-test`);
      setSensitivity(r.data);
      if (r.data.sensitivity_passed) {
        toast.success(`Prueba superada · Δ ${r.data.delta} pts entre giros`);
      } else {
        toast.warning(r.data.warning || 'Scores demasiado similares entre giros');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error ejecutando prueba de sensibilidad'); }
    finally { setRunningSensitivity(false); }
  };

  useEffect(() => { load(); }, [analysisId]);

  if (loading && !data) return (
    <div className="bg-white border border-slate-200 p-6" data-testid="industry-score-card">
      <div className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" />Calculando score específico…</div>
    </div>
  );
  if (!data) return null;

  return (
    <div className="bg-white border border-slate-200 p-6" data-testid="industry-score-card">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="label-caps flex items-center gap-1"><Target size={12} />Compatibilidad con tu negocio</div>
          <div className="text-xs text-slate-500 mt-1">Score específico por giro · distinto del potencial general de la zona</div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={runSensitivity}
              disabled={runningSensitivity}
              data-testid="run-sensitivity-btn"
              title="Ejecuta el mismo análisis con 6 giros diversos para validar que el score es realmente específico por giro."
            >
              {runningSensitivity ? <Loader2 size={12} className="mr-1 animate-spin" /> : <FlaskConical size={12} className="mr-1" />}
              Prueba de sensibilidad
            </Button>
          )}
          <BusinessProfileModal analysisId={analysisId} currentProfile={businessProfile} onSaved={load} />
        </div>
      </div>

      {isAdmin && sensitivity && (
        <div className="mb-4 border border-slate-200 bg-slate-50 p-4" data-testid="sensitivity-results">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FlaskConical size={14} className="text-blue-900" />
              <span className="font-semibold text-slate-900 text-sm">Prueba de sensibilidad · 6 giros diversos</span>
            </div>
            <span
              className={`text-[10px] px-2 py-1 font-semibold uppercase tracking-widest ${
                sensitivity.sensitivity_passed
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
              data-testid="sensitivity-status"
            >
              {sensitivity.sensitivity_passed ? `✓ Δ ${sensitivity.delta} pts` : `✗ Δ ${sensitivity.delta} pts`}
            </span>
          </div>
          {!sensitivity.sensitivity_passed && sensitivity.warning && (
            <div className="text-xs text-red-800 mb-2">{sensitivity.warning}</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {sensitivity.results?.map((r, i) => (
              <div key={i} className="bg-white border border-slate-200 p-2" data-testid={`sensitivity-row-${i}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{r.industry}</div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="font-display text-2xl font-bold" style={{ color: r.score >= 75 ? '#15803d' : r.score >= 55 ? '#1e3a8a' : r.score >= 40 ? '#b45309' : '#b91c1c' }}>
                    {r.score}
                  </span>
                  <span className="text-[10px] text-slate-500">{r.category}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-3 italic">
            Regla interna: la diferencia entre el score más alto y el más bajo debe ser ≥ 12 pts para confirmar que el motor personaliza por giro correctamente.
          </div>
        </div>
      )}

      {data.warning ? (
        <div className="bg-amber-50 border border-amber-200 p-4" data-testid="incomplete-profile-warning">
          <div className="flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-amber-700" />
            <span className="font-semibold text-amber-900">{data.warning}</span></div>
          <div className="text-sm text-slate-700">Completitud actual: <strong>{data.completeness.score}%</strong> ({data.completeness.level}). Agrega la descripción del negocio para desbloquear el análisis específico.</div>
          {data.fallback_score != null && (
            <div className="text-xs text-slate-500 mt-2">Score general de la zona: <strong>{data.fallback_score}</strong> ({data.fallback_category})</div>
          )}
        </div>
      ) : (
        <>
          {/* Main score + comparison */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className={`p-4 border ${CAT_COLOR[data.category] || 'bg-slate-50'}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">Score para {data.industry_key.replace(/_/g, ' ')}</div>
              <div className="font-display text-4xl font-bold mt-1">{data.score}<span className="text-lg opacity-60">/100</span></div>
              <div className="text-sm mt-1">{data.category_label}</div>
            </div>
            <div className="p-4 bg-slate-50 border border-slate-200">
              <div className="label-caps">Zona en general</div>
              <div className="font-display text-4xl font-bold text-slate-700 mt-1">{data.score_general_zone ?? '—'}<span className="text-lg text-slate-400">/100</span></div>
              <div className="text-sm text-slate-600 mt-1">{data.score_general_category || '—'}</div>
              {data.score_general_zone != null && (
                <div className="text-xs mt-2 font-semibold" style={{ color: data.score >= data.score_general_zone ? '#15803d' : '#b91c1c' }}>
                  {data.score >= data.score_general_zone ? '↑' : '↓'} Δ {(data.score - data.score_general_zone).toFixed(1)} vs. zona
                </div>
              )}
            </div>
          </div>

          {/* Completeness + confidence */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100 text-xs">
            <span className={`px-2 py-1 font-semibold uppercase tracking-widest text-[10px] ${LEVEL_COLOR[data.completeness.level] || 'bg-slate-500 text-white'}`}>
              Perfil {data.completeness.level} · {data.completeness.score}%
            </span>
            <span className="text-slate-600">Confianza del score: <strong>{(data.confidence * 100).toFixed(0)}%</strong></span>
            <span className="text-slate-400 ml-auto">{data.completeness.filled?.length || 0}/{data.completeness.total_variables} variables completas</span>
          </div>

          {/* Top drivers */}
          {data.top_drivers_positive?.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="label-caps flex items-center gap-1 text-green-800 mb-2"><TrendingUp size={12} />Impulsores positivos</div>
                {data.top_drivers_positive.slice(0, 4).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-xs" data-testid={`top-positive-${i}`}>
                    <span className="flex-1 truncate">{d.variable.replace(/_/g, ' ')}</span>
                    <span className="text-slate-500">w{d.weight}</span>
                    <span className="font-semibold" style={{ color: '#15803d' }}>{d.value.toFixed(0)}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="label-caps flex items-center gap-1 text-red-800 mb-2"><TrendingDown size={12} />Áreas débiles</div>
                {data.top_drivers_negative?.length > 0 ? data.top_drivers_negative.slice(0, 4).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-xs" data-testid={`top-negative-${i}`}>
                    <span className="flex-1 truncate">{d.variable.replace(/_/g, ' ')}</span>
                    <span className="text-slate-500">w{d.weight}</span>
                    <span className="font-semibold" style={{ color: '#b91c1c' }}>{d.value.toFixed(0)}</span>
                  </div>
                )) : <div className="text-xs text-slate-400 py-1">Ninguna variable crítica.</div>}
              </div>
            </div>
          )}

          {/* Adjustments applied */}
          {data.adjustments_applied?.length > 0 && (
            <details className="text-xs mt-3" data-testid="adjustments-details">
              <summary className="cursor-pointer text-slate-700 font-semibold flex items-center gap-1"><Sparkles size={11} />Ajustes aplicados por tu descripción ({data.adjustments_applied.length})</summary>
              <div className="mt-2 pl-3 border-l-2 border-blue-100 space-y-1">
                {data.adjustments_applied.map((a, i) => (
                  <div key={i} className="text-slate-600">• {a}</div>
                ))}
              </div>
            </details>
          )}

          {/* Methodology */}
          <details className="text-xs mt-2" data-testid="methodology-details">
            <summary className="cursor-pointer text-slate-500 flex items-center gap-1"><Info size={11} />¿Cómo se calculó esta calificación?</summary>
            <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1 text-slate-600">
              <div>Matriz: <strong>{data.methodology.matrix}</strong></div>
              <div>Fórmula: top 10 variables territoriales ponderadas + ajustes según perfil.</div>
              <div className="italic text-slate-500">{data.methodology.notes}</div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

// ============================================================
// Modal: extract profile from description
// ============================================================
function BusinessProfileModal({ analysisId, currentProfile, onSaved }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('form'); // 'form' | 'description'
  const [description, setDescription] = useState(currentProfile?.description || currentProfile?.notes || '');
  const [extracting, setExtracting] = useState(false);
  const [preview, setPreview] = useState(null);

  const doExtract = async () => {
    if (description.trim().length < 15) return toast.error('Descripción muy corta (mínimo 15 caracteres)');
    setExtracting(true); setPreview(null);
    try {
      const r = await api.post('/business-profile/extract', {
        description,
        industry_hint: currentProfile?.industry || currentProfile?.giro_principal || '',
      });
      setPreview(r.data);
      if (r.data.error) toast.error(r.data.error);
      else toast.success(`Perfil extraído · Completitud ${r.data.completeness?.score}%`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setExtracting(false); }
  };

  const doSave = async () => {
    if (!preview?.profile) return;
    try {
      const p = preview.profile;
      // Merge into the business_profile of the analysis
      await api.patch(`/business-profiles/${currentProfile?.id || currentProfile?.business_profile_id}`, {
        description,
        giro_principal: p.giro_principal || currentProfile?.industry,
        productos: p.productos,
        cliente_objetivo: p.cliente_objetivo,
        ticket_promedio: p.ticket_promedio,
        tipo_compra: p.tipo_compra,
        posicionamiento: p.posicionamiento,
        depende_flujo_peatonal: p.depende_flujo_peatonal,
        depende_flujo_vehicular: p.depende_flujo_vehicular,
        necesita_estacionamiento: p.necesita_estacionamiento,
        depende_negocios_complementarios: p.depende_negocios_complementarios,
        sensibilidad_competencia: p.sensibilidad_competencia,
        depende_turismo: p.depende_turismo,
        depende_oficinas: p.depende_oficinas,
        depende_escuelas: p.depende_escuelas,
        depende_vivienda: p.depende_vivienda,
        nivel_especializacion: p.nivel_especializacion,
        radio_atraccion_km: p.radio_atraccion_km,
        marcas_competidoras: p.marcas_competidoras,
        negocios_sustitutos: p.negocios_sustitutos,
        negocios_complementarios: p.negocios_complementarios,
        diferenciadores: p.diferenciadores,
        zona_ideal: p.zona_ideal,
        plaza_ideal: p.plaza_ideal,
      });
      toast.success('Perfil guardado. Recalculando score…');
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) { toast.error('Error guardando perfil — usa la vista general del análisis'); }
  };

  const p = preview?.profile;
  const c = preview?.completeness;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-profile-modal">
          <Settings size={12} className="mr-1" />Actualizar perfil del negocio
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-blue-900" />Perfil Estratégico del Negocio</DialogTitle>
          <DialogDescription>Elige un formulario estructurado (18 preguntas) o una descripción libre que la IA convertirá en variables.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-slate-200 -mt-2 mb-2">
          <button
            data-testid="mode-form-tab"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest border-b-2 ${mode === 'form' ? 'text-blue-900 border-blue-900' : 'text-slate-500 border-transparent hover:text-slate-800'}`}
            onClick={() => setMode('form')}
          ><FileText size={12} className="inline mr-1" />Formulario estructurado</button>
          <button
            data-testid="mode-description-tab"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest border-b-2 ${mode === 'description' ? 'text-blue-900 border-blue-900' : 'text-slate-500 border-transparent hover:text-slate-800'}`}
            onClick={() => setMode('description')}
          ><Sparkles size={12} className="inline mr-1" />Descripción libre + IA</button>
        </div>

        {mode === 'form' ? (
          <ProfileForm
            analysisId={analysisId}
            currentProfile={currentProfile}
            onSaved={() => { setOpen(false); onSaved && onSaved(); }}
          />
        ) : (
        <div className="space-y-4">
          <div>
            <label className="label-caps mb-2 block">Describe tu negocio</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="profile-description"
              placeholder="Ej: Perfumería premium con perfumes de nicho, contratipos árabes y fragancias exclusivas. Ticket promedio $850, cliente mujer 25-45 NSE AB, compra planeada y por impulso. Necesita alto flujo peatonal, cercanía a plazas comerciales y tiendas ancla…"
              className="h-40 text-sm" />
            <div className="text-xs text-slate-500 mt-1">Cuanto más detalle (giro, cliente, ticket, tipo de compra, dependencias), mejor será el score específico.</div>
          </div>

          <Button onClick={doExtract} disabled={extracting || description.trim().length < 15} data-testid="extract-btn" className="bg-blue-900 hover:bg-blue-800">
            {extracting ? <><Loader2 size={14} className="mr-2 animate-spin" />Extrayendo perfil con IA…</> : <><Sparkles size={14} className="mr-2" />Extraer perfil estructurado</>}
          </Button>

          {preview && !preview.error && p && (
            <div className="border border-slate-200 p-4 space-y-3" data-testid="profile-preview">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${LEVEL_COLOR[c?.level] || 'bg-slate-500 text-white'}`}>
                  Completitud {c?.score}% ({c?.level})
                </span>
                <span className="text-xs text-slate-500">{c?.filled?.length}/{c?.total_variables} variables llenas</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <ProfileRow label="Giro principal" value={p.giro_principal} />
                <ProfileRow label="Ticket promedio" value={p.ticket_promedio ? `$${p.ticket_promedio}` : null} />
                <ProfileRow label="Cliente" value={p.cliente_objetivo} />
                <ProfileRow label="Posicionamiento" value={p.posicionamiento} />
                <ProfileRow label="Tipo compra" value={p.tipo_compra} />
                <ProfileRow label="Modelo venta" value={p.modelo_venta} />
                <ProfileRow label="Flujo peatonal" value={p.depende_flujo_peatonal ? 'Sí, alto' : 'No crítico'} />
                <ProfileRow label="Estacionamiento" value={p.necesita_estacionamiento ? 'Requerido' : 'No crítico'} />
                <ProfileRow label="Sensibilidad competencia" value={p.sensibilidad_competencia} />
                <ProfileRow label="Depende turismo" value={p.depende_turismo ? 'Sí' : 'No'} />
              </div>
              {preview.reasoning && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-500">Razonamiento de la IA</summary>
                  <div className="mt-1 text-slate-600 italic">{preview.reasoning}</div>
                </details>
              )}
              <Button onClick={doSave} data-testid="save-profile-btn" className="w-full bg-blue-900 hover:bg-blue-800">
                <CheckCircle2 size={14} className="mr-2" />Guardar y recalcular score
              </Button>
            </div>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ProfileRow = ({ label, value }) => (
  <div className="flex justify-between border-b border-slate-100 py-1">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-900 truncate ml-2">{value || <span className="text-slate-300 font-normal italic">—</span>}</span>
  </div>
);
